/**
 * @taskId T649
 * @taskId T651
 * @see functional/Cycles/cycle-5/tasks.md HU-25-FE
 */
import { authFetch } from '@flexibilitypay/shared';

// ─── Period ──────────────────────────────────────────────────────────────────

export type DashboardPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'QUARTER';

export const PERIOD_OPTIONS: { value: DashboardPeriod; label: string }[] = [
  { value: 'TODAY', label: 'Hoy' },
  { value: 'WEEK', label: '7 días' },
  { value: 'MONTH', label: '30 días' },
  { value: 'QUARTER', label: 'Trimestre' },
];

export const DEFAULT_PERIOD: DashboardPeriod = 'MONTH';

/**
 * Type guard — T651: valida que un valor sea un período permitido.
 *
 * Criterio: "Período seleccionable solo entre valores permitidos
 * (TODAY/WEEK/MONTH/QUARTER). Default: MONTH" — HU-25-FE.
 */
export function isValidPeriod(value: unknown): value is DashboardPeriod {
  return (
    typeof value === 'string' &&
    (PERIOD_OPTIONS as { value: string }[]).some((opt) => opt.value === value)
  );
}

/**
 * Devuelve el período si es válido, o el período por defecto.
 * Útil para sanitizar valores externos (query params, localStorage, etc.).
 */
export function toValidPeriod(value: unknown): DashboardPeriod {
  return isValidPeriod(value) ? value : DEFAULT_PERIOD;
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface BreakdownItem {
  merchantId: string;
  merchantName: string;
  total: number;
  successRate: number;
}

/**
 * Respuesta del endpoint GET /api/v1/bo/dashboard/kpis.
 *
 * Nota de precisión (P-02): los campos monetarios (`aum`) se tipan como `number`
 * porque JSON no puede representar BigDecimal. El backend debe garantizar que
 * `aum < Number.MAX_SAFE_INTEGER` (9.007 × 10^15 ARS) para evitar pérdida de
 * precisión IEEE-754. Si en el futuro se supera ese umbral, migrar a `string`
 * con conversión explícita vía `BigInt` o `Decimal.js`.
 *
 * @see Constitution P-02 — BigDecimal obligatorio en backend; frontend usa number.
 */
export interface DashboardKpiResponse {
  /** Saldo total bajo administración — ver nota de precisión arriba. */
  aum: number;
  merchantsActive: number;
  /** Porcentaje de transacciones aprobadas (0–100). */
  successRate: number;
  alertsAml: number;
  aumVariationPercent: number | null;
  merchantsActiveVariationPercent: number | null;
  successRateVariationPercent: number | null;
  alertsAmlVariationPercent: number | null;
  breakdown: BreakdownItem[];
  periodLabel: string;
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

export async function fetchDashboardKpis(
  period: DashboardPeriod,
): Promise<DashboardKpiResponse> {
  const params = new URLSearchParams({ period });
  const res = await authFetch(`/api/v1/bo/dashboard/kpis?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`HTTP_${res.status}`);
  }
  return (await res.json()) as DashboardKpiResponse;
}
